import * as React from 'react';
import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CustomizedDataGrid from './DocumentsDataGrid';
import CircularProgress from '@mui/material/CircularProgress';
import AnalyticsGrid from './AnalyticsGrid';
import { useAuth } from '../../authentication/AuthContext';
import axiosInstance from '../../authentication/AuthContext';
import PreferencesGrid from './PreferencesGrid';
import UserActivityGrid from './UserActivityGrid';
import RecentLoginActivityGrid from './RecentLoginActivityGrid';

export default function MainGrid() {
    const { accessToken, dashboardData } = useAuth();
    const [activityData, setActivityData] = useState({
        recentLogins: [],
        totalUsers: 0,
        activeUsers: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch activity data
    useEffect(() => {
        const fetchActivityData = async () => {
            try {
                setLoading(true);

                // Fetch login activity (API returns array of { id, username, user_id, timestamp, ip_address })
                const response = await axiosInstance.get('/accounts/activity/');
                const activities = Array.isArray(response.data)
                    ? response.data
                    : response.data?.activities || [];

                // Calculate stats from login data
                const uniqueUsers = [
                    ...new Set(activities.map((a) => a.user_id)),
                ];
                const today = new Date();
                const todayStart = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate()
                );
                const activeToday = activities.filter(
                    (a) => new Date(a.timestamp) >= todayStart
                );

                setActivityData({
                    recentLogins: activities.filter(
                        (a) => a.activity_type === 'login'
                    ),
                    totalUsers: uniqueUsers.length,
                    activeUsers: activeToday.length,
                });
            } catch (err) {
                console.error('Failed to fetch activity data:', err);
                setError('Failed to load activity data');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchActivityData();
        }
    }, [accessToken]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Check if user has access to specific features
    const hasAccess = (feature) => {
        if (!dashboardData?.allowed_endpoints) return false;
        return dashboardData.allowed_endpoints.some((endpoint) =>
            endpoint.name.toLowerCase().includes(feature.toLowerCase())
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Role-based Grid Layout - full width, responsive stack on mobile */}
            <Grid container spacing={3} sx={{ mb: 3, width: '100%' }}>
                {(hasAccess('User Management') || hasAccess('My Activity')) && (
                    <Grid item size={12}>
                        <UserActivityGrid activityData={activityData} />
                    </Grid>
                )}
            </Grid>

            <Grid
                container
                spacing={3}
                columns={12}
                sx={{ mb: 3, width: '100%' }}
            >
                {hasAccess('Preferences') && (
                    <Grid item size={6}>
                        <Box
                            sx={{
                                height: 400,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <PreferencesGrid />
                        </Box>
                    </Grid>
                )}
                {(hasAccess('User Management') || hasAccess('My Activity')) && (
                    <Grid item size={hasAccess('Preferences') ? 6 : 12}>
                        <RecentLoginActivityGrid
                            activityData={activityData}
                            isAdmin={hasAccess('User Management')}
                            errorOccured={error}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Documents Section - Show for all roles */}
            {hasAccess('Document Management') || hasAccess('View Documents') ? (
                <Grid container spacing={3} sx={{ width: '100%' }}>
                    <Grid item size={12}>
                        <CustomizedDataGrid />
                    </Grid>
                </Grid>
            ) : null}

            {/* Analytics - Show for admin only */}
            {hasAccess('Analytics') && (
                <Grid container spacing={3} sx={{ width: '100%' }}>
                    <Grid item size={12}>
                        <AnalyticsGrid />
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
